import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, BookOpen, Inbox, Users, Map, LogOut, Menu, UsersRound, ListChecks, GraduationCap, Award, BarChart3, ClipboardList, Sparkles, Bot, BellRing, Images, Settings, ShieldCheck, MessageSquare, FileStack, FileCheck2, ListTree, HelpCircle } from "lucide-react";
import { QuickAsk } from "@/components/assistant/QuickAsk";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/brand/Logo";
import { RoleBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NavItem = ({ to, icon: Icon, label, testId, badge }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      `group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${
        isActive ? "bg-lasso-ink text-white" : "text-zinc-600 hover:bg-lasso-tint hover:text-lasso-ink"
      }`
    }
  >
    <Icon className="h-4 w-4" />
    <span className="flex-1">{label}</span>
    {badge > 0 && (
      <span className="rounded-full bg-lasso-sky px-2 py-0.5 text-[10px] font-bold text-lasso-ink" data-testid={`${testId}-badge`}>
        {badge}
      </span>
    )}
  </NavLink>
);

const Group = ({ label, children }) => (
  <div className="space-y-1">
    <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-zinc-400">{label}</p>
    {children}
  </div>
);

function SidebarBody() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [pending, setPending] = useState(0);

  useEffect(() => {
    if (user?.role !== "admin") return;
    if (user.role === "admin" || user.is_region_admin) api.get("/admin/access-requests", { params: { status: "pending" } }).then((r) => setPending(r.data.length)).catch(() => {});
  }, [user, location.pathname]);

  return (
    <div className="flex h-full flex-col">
      <div className="px-5 pb-8 pt-7">
        <Logo size="sm" testId="sidebar-logo" />
      </div>
      <nav className="flex-1 space-y-7 overflow-y-auto px-3">
        <Group label="Workspace">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testId="sidebar-nav-dashboard" />
          <NavItem to="/ask" icon={Sparkles} label="Ask Lasso" testId="sidebar-nav-ask" />
          <NavItem to="/training" icon={ListChecks} label="My Training" testId="sidebar-nav-training" />
          <NavItem to="/courses" icon={GraduationCap} label="Courses" testId="sidebar-nav-courses" />
          <NavItem to="/sops" icon={BookOpen} label="SOP Library" testId="sidebar-nav-sops" />
          <NavItem to="/forms" icon={FileStack} label="Forms" testId="sidebar-nav-forms" />
          <NavItem to="/certificates" icon={Award} label="Certificates" testId="sidebar-nav-certificates" />
        </Group>
        {["admin", "regional_manager", "community_manager"].includes(user.role) && (
          <Group label="Team training">
            <NavItem to="/reports" icon={BarChart3} label="Progress" testId="sidebar-nav-reports" />
            {user.role !== "community_manager" && <NavItem to="/assignments" icon={ClipboardList} label="Assignments" testId="sidebar-nav-assignments" />}
            {user.role !== "community_manager" && <NavItem to="/compliance" icon={ShieldCheck} label="Compliance" testId="sidebar-nav-compliance" />}
            {(user.role === "admin" || user.is_region_admin) && <NavItem to="/insights/exams" icon={BarChart3} label="Exam insights" testId="sidebar-nav-exam-insights" />}
            <NavItem to="/form-records" icon={FileCheck2} label="Form records" testId="sidebar-nav-form-records" />
            <NavItem to="/feedback" icon={MessageSquare} label="Feedback" testId="sidebar-nav-feedback" />
          </Group>
        )}
        {user.role === "regional_manager" && (
          <Group label="My Region">
            <NavItem to="/team" icon={UsersRound} label="My Team" testId="sidebar-nav-team" />
            {user.is_region_admin && <NavItem to="/admin/users" icon={Users} label="Manage employees" testId="sidebar-nav-region-users" />}
            {user.is_region_admin && <NavItem to="/admin/requests" icon={Inbox} label="Access Requests" testId="sidebar-nav-region-requests" badge={pending} />}
            {user.is_region_admin && <NavItem to="/dashboard?guide=region-admin" icon={HelpCircle} label="Region Admin guide" testId="sidebar-nav-region-guide" />}
          </Group>
        )}
        {user.role === "admin" && (
          <Group label="Admin">
            <NavItem to="/admin/requests" icon={Inbox} label="Access Requests" testId="sidebar-nav-requests" badge={pending} />
            <NavItem to="/admin/users" icon={Users} label="Users" testId="sidebar-nav-users" />
            <NavItem to="/admin/org" icon={Map} label="Regions & Communities" testId="sidebar-nav-communities" />
            {user.is_super && <NavItem to="/admin/ai" icon={Bot} label="AI Assistant" testId="sidebar-nav-ai" />}
            {user.is_super && <NavItem to="/admin/notifications" icon={BellRing} label="Notifications" testId="sidebar-nav-notifications" />}
            <NavItem to="/admin/showcase" icon={Images} label="Showcase" testId="sidebar-nav-showcase" />
            <NavItem to="/admin/event-types" icon={ListTree} label="Required forms" testId="sidebar-nav-event-types" />
            <NavItem to="/admin/feedback" icon={Inbox} label="Feedback inbox" testId="sidebar-nav-feedback-inbox" />
          </Group>
        )}
      </nav>
      <div className="m-3 rounded-xl border border-lasso-line bg-lasso-paper p-4" data-testid="sidebar-user-card">
        <p className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user.name}</p>
        <p className="truncate text-xs text-muted-foreground">{user.email}</p>
        <NavLink to="/profile" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-lasso-deep hover:text-lasso-ink" data-testid="sidebar-profile-link">
          <Settings className="h-3.5 w-3.5" /> Preferences
        </NavLink>
        <div className="mt-3 flex items-center justify-between gap-2">
          <RoleBadge role={user.is_super ? "super_admin" : user.role} regionAdmin={user.is_region_admin} testId="sidebar-user-role" />
          <button
            onClick={logout}
            data-testid="sidebar-logout-button"
            className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-semibold text-zinc-500 transition-colors hover:text-lasso-ink"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="min-h-screen bg-lasso-paper">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-lasso-line bg-white lg:block" data-testid="app-sidebar">
        <SidebarBody />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-lasso-line bg-white/80 px-5 py-4 backdrop-blur-xl lg:hidden">
        <Logo size="sm" />
        <div className="flex items-center gap-2">
        <NotificationBell prefix="mobile-" />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="rounded-lg border border-lasso-line p-2">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SidebarBody />
          </SheetContent>
        </Sheet>
        </div>
      </header>
      <main className="lg:pl-64">
        <div className="sticky top-0 z-30 hidden items-center justify-end border-b border-lasso-line bg-white/80 px-8 py-3 backdrop-blur-xl lg:flex" data-testid="app-topbar">
          <NotificationBell />
        </div>
        <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8 lg:px-12 lg:py-14">
          <Outlet />
        </div>
      </main>
      <QuickAsk />
    </div>
  );
}
